import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  History,
  RefreshCw,
  Trash2,
  Check,
  X as XIcon,
  Filter,
  Shield,
  ShieldAlert,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('az-AZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
};

const LoginAttemptsViewer = ({ token, allowedIps }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'success' | 'fail'
  const [err, setErr] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  const fetchAttempts = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = filter === 'all' ? {} : { only: filter };
      const res = await axios.get(`${API}/admin/login-attempts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 200, ...params },
      });
      setItems(res.data?.items || []);
    } catch (e) {
      setErr('Loqları gətirmək alınmadı: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    if (token) fetchAttempts();
  }, [fetchAttempts, token]);

  const handleClear = async () => {
    try {
      await axios.delete(`${API}/admin/login-attempts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems([]);
      setConfirmClear(false);
    } catch (e) {
      setErr('Silmək alınmadı: ' + (e.response?.data?.detail || e.message));
    }
  };

  const successCount = items.filter((i) => i.success).length;
  const failCount = items.length - successCount;
  const allowedSet = new Set(allowedIps || []);

  return (
    <div data-testid="login-attempts-viewer" style={{ marginTop: 28 }}>
      <h3 className="admin-section-title flex items-center gap-2">
        <History size={16} /> Login cəhdləri loqu
      </h3>
      <p className="admin-section-desc">
        Son admin login cəhdləri (uğurlu və uğursuz). Sistem ən son 1000 girişi saxlayır.
      </p>

      {/* Filter + actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { id: 'all', label: 'Hamısı', count: items.length },
            { id: 'success', label: 'Uğurlu', count: filter === 'all' ? successCount : null },
            { id: 'fail', label: 'Uğursuz', count: filter === 'all' ? failCount : null },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`admin-tab ${filter === f.id ? 'admin-tab-active' : ''}`}
              data-testid={`attempts-filter-${f.id}`}
              style={{ padding: '6px 12px' }}
            >
              <Filter size={12} /> {f.label}
              {f.count !== null && f.count !== undefined && (
                <span style={{ opacity: 0.6, marginLeft: 4 }}>({f.count})</span>
              )}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={fetchAttempts}
          className="admin-btn-secondary"
          disabled={loading}
          data-testid="attempts-refresh-btn"
          title="Yenidən yüklə"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
        <div style={{ flex: 1 }} />
        {items.length > 0 && !confirmClear && (
          <button
            type="button"
            onClick={() => setConfirmClear(true)}
            className="admin-btn-secondary"
            data-testid="attempts-clear-btn"
            style={{ color: '#fca5a5', borderColor: 'rgba(239,68,68,0.3)' }}
          >
            <Trash2 size={14} /> Loqu təmizlə
          </button>
        )}
        {confirmClear && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#fca5a5' }}>Əminsiniz?</span>
            <button
              type="button"
              onClick={handleClear}
              className="admin-btn-primary"
              style={{ background: 'rgba(239,68,68,0.2)', borderColor: 'rgba(239,68,68,0.5)', color: '#fca5a5' }}
              data-testid="attempts-clear-confirm"
            >
              Bəli, sil
            </button>
            <button
              type="button"
              onClick={() => setConfirmClear(false)}
              className="admin-btn-secondary"
              data-testid="attempts-clear-cancel"
            >
              Ləğv
            </button>
          </div>
        )}
      </div>

      {err && (
        <div
          style={{
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.3)',
            color: '#fca5a5',
            padding: '10px 14px',
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 13,
          }}
          data-testid="attempts-error"
        >
          {err}
        </div>
      )}

      {/* Table */}
      <div
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {items.length === 0 ? (
          <div
            style={{
              padding: '32px 16px',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.4)',
              fontSize: 13,
            }}
            data-testid="attempts-empty"
          >
            {loading ? 'Yüklənir...' : 'Hələ heç bir login cəhdi qeydə alınmayıb.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr
                  style={{
                    background: 'rgba(168, 85, 247, 0.08)',
                    color: '#f0e68c',
                    textAlign: 'left',
                  }}
                >
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Vaxt</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>İstifadəçi</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>IP</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Səbəb</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Cihaz</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const isWhitelisted = allowedSet.has(it.ip);
                  const isSuspicious = !it.success && !isWhitelisted;
                  return (
                    <tr
                      key={idx}
                      data-testid={`attempt-row-${idx}`}
                      style={{
                        borderTop: '1px solid rgba(255,255,255,0.04)',
                        background: isSuspicious ? 'rgba(239, 68, 68, 0.04)' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '10px 12px' }}>
                        {it.success ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              color: '#86efac',
                              fontWeight: 600,
                            }}
                            data-testid="attempt-status-success"
                          >
                            <Check size={14} /> OK
                          </span>
                        ) : (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              color: '#fca5a5',
                              fontWeight: 600,
                            }}
                            data-testid="attempt-status-fail"
                          >
                            <XIcon size={14} /> Fail
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          fontFamily: 'monospace',
                          whiteSpace: 'nowrap',
                          color: 'rgba(255,255,255,0.75)',
                        }}
                      >
                        {formatTimestamp(it.timestamp)}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#c4b5fd' }}>{it.username || '—'}</td>
                      <td
                        style={{
                          padding: '10px 12px',
                          fontFamily: 'monospace',
                          color: '#e2e8f0',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {isWhitelisted ? (
                            <Shield size={12} style={{ color: '#86efac' }} />
                          ) : (
                            isSuspicious && <ShieldAlert size={12} style={{ color: '#fca5a5' }} />
                          )}
                          {it.ip || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.6)' }}>
                        {it.reason || '—'}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          color: 'rgba(255,255,255,0.4)',
                          maxWidth: 240,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={it.user_agent}
                      >
                        {it.user_agent || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginAttemptsViewer;
